import { createContext, useContext, useEffect, useState, useRef } from "react";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { ensureUserDoc } from "../lib/userProfile";
import { app, db, auth } from "../firebase";

const AuthContext = createContext();

// ✅ Normalize timestamp to milliseconds (handles Timestamp, number, ISO string)
function toMillis(tsLike) {
  if (!tsLike) return null;
  // Firestore Timestamp
  if (typeof tsLike?.toMillis === "function") return tsLike.toMillis();
  // number ms
  if (typeof tsLike === "number") return tsLike;
  // ISO string or Date
  const d = new Date(tsLike);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

// ✅ Build enriched user doc with derived fields (no mutation)
function buildUserDoc(rawUserData) {
  if (!rawUserData) return null;

  const trialEndsAtMs = toMillis(rawUserData?.trialEndsAt);
  const isTrialPlan = rawUserData?.plan === "trial" || rawUserData?.plan === "trial_verified";
  const isTrialActive = isTrialPlan && !!trialEndsAtMs && trialEndsAtMs > Date.now();

  return {
    ...rawUserData,
    // ✅ Derived fields (display-only, not persisted)
    trialEndsAtMs,
    isTrialPlan,
    isTrialActive,
    isTrialExpired: isTrialPlan && !!trialEndsAtMs && trialEndsAtMs <= Date.now(),
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userDocError, setUserDocError] = useState(null); // Track Firestore errors

  // ✅ Use useRef for unsubscribe (prevents stale closure issues)
  const unsubUserDocRef = useRef(null);

  // Add timeout to prevent infinite loading (only if we have no user at all)
  useEffect(() => {
    const loadingTimeout = setTimeout(() => {
      if (loading && !user) {
        // Only timeout if there's no Firebase auth user
        // If we have a user but waiting for userDoc (like after payment), keep waiting
        console.warn("Authentication loading timeout - forcing complete");
        setUser(null);
        setUserDoc(null);
        setLoading(false);
      }
    }, 15000); // 15 seconds - allow time for webhook to process

    return () => clearTimeout(loadingTimeout);
  }, [loading, user]);

  useEffect(() => {
    let isSubscribed = true; // Track if component is still mounted
    
    const unsubAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (!isSubscribed) return; // Component unmounted, ignore
      
      // Only log in development
      if (import.meta.env.DEV) {
        console.log("🔥 Firebase Auth State Changed:", {
          hasUser: !!currentUser,
          uid: currentUser?.uid?.substring(0, 8) + '...',
          isSubscribed
        });
      }
      setUser(currentUser);
      setUserDocError(null); // ✅ Clear any previous errors when auth state changes

      if (currentUser) {
        try {
          const userRef = doc(db, "users", currentUser.uid);

          // Set up real-time listener for user document immediately
          const unsubDoc = onSnapshot(userRef, async (userSnap) => {
            if (!isSubscribed) return; // Component unmounted, ignore

            if (userSnap.exists()) {
              const rawUserData = userSnap.data();
              setUserDocError(null); // ✅ Clear error on successful load

              if (import.meta.env.DEV) {
                console.log("User document loaded:", rawUserData.role, rawUserData.status);
              }

              // ✅ Build enriched doc with derived fields (no mutation)
              const enrichedUserDoc = buildUserDoc(rawUserData);

              if (import.meta.env.DEV && enrichedUserDoc.isTrialExpired) {
                console.log("Trial has expired for user:", rawUserData.email);
              }

              setUserDoc(enrichedUserDoc);
              setLoading(false);
            } else {
              if (import.meta.env.DEV) {
                console.log("User document missing - registration flow must create it");
              }
              // ✅ Do nothing. Registration flow (TeacherRegister/StudentRegister) creates the doc.
              // Don't auto-create here to avoid race conditions.
              setUserDoc(null);
              setLoading(false);
            }
          }, (error) => {
            console.error("🔥 Firestore listener error:", error);
            console.error("Error code:", error.code);
            console.error("Error message:", error.message);

            if (isSubscribed) {
              // Store the error so ProtectedRoute can show proper message
              setUserDocError(error);
              setUserDoc(null); // Don't set fake data - let ProtectedRoute handle it
              setLoading(false);
            }
          });

          // ✅ Store unsubscribe using ref (prevents ghost listeners)
          unsubUserDocRef.current = unsubDoc;
        } catch (error) {
          console.error("🔥 Error setting up user listener:", error);
          if (isSubscribed) {
            setUserDocError(error);
            setUserDoc(null);
            setLoading(false);
          }
        }
      } else {
        setUserDoc(null);
        setLoading(false);
      }
    });

    return () => {
      isSubscribed = false; // Mark component as unmounted
      unsubAuth();
      // ✅ Clean up user doc listener using ref
      if (unsubUserDocRef.current) {
        unsubUserDocRef.current();
        unsubUserDocRef.current = null;
      }
    };
  }, []);

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserDoc(null);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Maintain backward compatibility
  const userRole = userDoc?.role || null;

  return (
    <AuthContext.Provider value={{ user, userDoc, userRole, loading, userDocError, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook for easy access
export const useAuth = () => useContext(AuthContext);

// ✅ Export helpers for use in other components
export { toMillis, buildUserDoc };